import { FlagTriangleRight, GitBranch, MessageCircleQuestion, Play, Repeat } from "lucide-react";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "../../utils";
import type { FlowLink } from "./processGraphModel";
import { CollapsibleSidebarSection, ShowAllButton, StepAppIcon } from "./processSidebarUi";

const DirectionList = ({
  incoming,
  links,
  step,
  start,
  end,
  appById,
  onSelectStep,
  onHoverBranch,
  onAsk,
}: {
  incoming: boolean;
  links: FlowLink[];
  step: { id: string; name: string };
  start: string;
  end: string;
  appById: Map<string, string | null>;
  onSelectStep?: (id: string) => void;
  onHoverBranch?: (edge: { from: string; to: string } | null) => void;
  onAsk: (step: { id: string; name: string }, branch?: FlowLink) => void;
}) => {
  const { t } = useTranslation();
  useEffect(() => () => onHoverBranch?.(null), [onHoverBranch]);
  const [expanded, setExpanded] = useState(false);
  const total = links.reduce((sum, link) => sum + link.count, 0);
  const title = t(incoming ? "processPanel.stepPrevious" : "step_next_steps");
  return (
    <section className="min-w-0" aria-label={title}>
      <div className="flex flex-wrap items-baseline justify-between gap-1">
        <h4 className="text-sm font-semibold text-text">{title}</h4>
        <span className="text-xs tabular-nums text-text-dim">
          {t(incoming ? "processPanel.stepArrivals" : "processPanel.stepDepartures", {
            count: total,
          })}
        </span>
      </div>
      {links.length === 0 ? (
        <p className="mt-3 text-xs text-text-dim">{t("processPanel.stepNoTransitions")}</p>
      ) : (
        <ul className="mt-2 divide-y divide-outline">
          {(expanded ? links : links.slice(0, 3)).map(link => {
            const self = link.id === step.id;
            const terminal = link.id === start || link.id === end;
            const label = self
              ? t("processPanel.branchRepeats")
              : terminal
                ? t(link.id === start ? "processPanel.caseStart" : "processPanel.caseEnd")
                : link.name;
            const app = appById.get(link.id);
            const edge = incoming ? { from: link.id, to: step.id } : { from: step.id, to: link.id };
            const Icon = self ? Repeat : link.id === start ? Play : FlagTriangleRight;
            const content = (
              <>
                <span className="mt-0.5 shrink-0">
                  {self || terminal ? (
                    <Icon className="h-4 w-4 text-text-dim" aria-hidden="true" />
                  ) : (
                    <StepAppIcon app={app} />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0 break-words font-medium text-text">{label}</span>
                    <span className="shrink-0 font-semibold tabular-nums text-text">
                      {link.pct}%
                    </span>
                  </span>
                  <span className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-text-dim">
                    {!terminal && app && <span>{app}</span>}
                    <span>{t("processPanel.stepTransitionCount", { count: link.count })}</span>
                    <span>{t("processPanel.stepTransitionCases", { count: link.cases })}</span>
                  </span>
                  <span
                    className="mt-2 block h-1.5 overflow-hidden rounded-full bg-app-usage-track"
                    aria-hidden="true"
                  >
                    <span
                      className="block h-full rounded-full bg-app-usage/70"
                      style={{ width: `${link.pct}%` }}
                    />
                  </span>
                  {link.averageWaitSeconds !== null && link.averageWaitSeconds >= 120 && (
                    <span className="mt-1.5 block text-xs text-text-mid">
                      {t("processPanel.stepTransitionWait", {
                        duration: formatDuration(link.averageWaitSeconds),
                      })}
                    </span>
                  )}
                </span>
              </>
            );
            return (
              <li key={link.id} className="py-2">
                {!terminal && !self && onSelectStep ? (
                  <button
                    type="button"
                    onClick={() => onSelectStep(link.id)}
                    onPointerEnter={() => onHoverBranch?.(edge)}
                    onPointerLeave={() => onHoverBranch?.(null)}
                    onFocus={() => onHoverBranch?.(edge)}
                    onBlur={() => onHoverBranch?.(null)}
                    className="flex w-full gap-2 rounded-md p-1 text-left text-sm hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex gap-2 p-1 text-sm">{content}</div>
                )}
                {!incoming && !terminal && (
                  <button
                    type="button"
                    aria-label={t("ask_why_transition", {
                      from: incoming ? link.name : step.name,
                      to: incoming ? step.name : link.name,
                    })}
                    onClick={() => onAsk(step, link)}
                    className="ml-7 mt-1 inline-flex min-h-7 items-center gap-1.5 rounded px-1 text-xs text-info hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("processPanel.stepAskTransition")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {links.length > 3 && (
        <ShowAllButton
          expanded={expanded}
          label={t(incoming ? "processPanel.stepAllPrevious" : "processPanel.stepAllNext", {
            count: links.length,
          })}
          onClick={() => setExpanded(value => !value)}
        />
      )}
    </section>
  );
};

export default function ProcessStepTransitions(
  props: Omit<React.ComponentProps<typeof DirectionList>, "incoming" | "links"> & {
    incoming: FlowLink[];
    outgoing: FlowLink[];
  },
) {
  const { t } = useTranslation();
  return (
    <CollapsibleSidebarSection
      title={t("processPanel.stepSequence")}
      headerIcon={<GitBranch className="h-4 w-4" aria-hidden="true" />}
      summary={t("processPanel.stepSequenceSummary", {
        previous: props.incoming.length,
        next: props.outgoing.length,
      })}
      defaultOpen={false}
      separated={false}
      className="border-t border-outline pt-4"
    >
      <p className="mb-3 text-xs leading-relaxed text-text-mid">
        {t("processPanel.stepTransitionsHelp")}
      </p>
      <div className="@container">
        <div className="grid grid-cols-1 gap-5 @2xl:grid-cols-2">
          <DirectionList {...props} incoming links={props.incoming} />
          <DirectionList {...props} incoming={false} links={props.outgoing} />
        </div>
      </div>
    </CollapsibleSidebarSection>
  );
}
